export type SaveResult = { ok: true } | { ok: false; error: string }

export interface EditorField {
  id: string
  kind: 'wysiwyg' | 'input'
  label: string
}

export interface EditorSession extends EditorField {
  loading: boolean
  dirty: boolean
  saving: boolean
  error: string | null
}

export interface EditorActions {
  save: () => Promise<SaveResult>
  contentLink?: () => void
}

export function createEditorStore() {
  let session: EditorSession | null = null
  let actions: EditorActions | null = null
  const listeners = new Set<() => void>()
  const emit = () => listeners.forEach((listener) => listener())
  const patch = (
    id: string,
    value: Partial<Pick<EditorSession, 'loading' | 'dirty' | 'error'>>
  ) => {
    if (session?.id !== id) return
    session = { ...session, ...value }
    emit()
  }

  return {
    getState: () => session,
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    open(field: EditorField) {
      if (session) return false
      session = {
        ...field,
        loading: true,
        dirty: false,
        saving: false,
        error: null,
      }
      emit()
      return true
    },
    patch,
    register(id: string, value: EditorActions) {
      if (session?.id !== id) return () => undefined
      actions = value
      return () => {
        if (actions === value) actions = null
      }
    },
    cancel() {
      if (session?.saving) return
      session = null
      actions = null
      emit()
    },
    contentLink() {
      if (session && !session.loading && !session.saving)
        actions?.contentLink?.()
    },
    async save() {
      if (
        !session ||
        session.loading ||
        session.saving ||
        !session.dirty ||
        !actions
      )
        return
      const id = session.id
      session = { ...session, saving: true, error: null }
      emit()
      let result: SaveResult
      try {
        result = await actions.save()
      } catch {
        result = { ok: false, error: `Unable to save. Please try again.` }
      }
      if (session?.id !== id) return
      if (result.ok) {
        session = null
        actions = null
      } else {
        session = { ...session, saving: false, error: result.error }
      }
      emit()
    },
  }
}
